import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ADMIN_TOKEN_KEY, api, formatPhoneInput } from '../api';
import { AppSettings, TableRecord } from '../types';
import TableItem from './TableItem';
import './AdminDashboard.css';

const DEFAULT_SETTINGS: AppSettings = {
  venueTitle: '공인대 주점',
  maxTableNumber: 65,
  defaultDurationMinutes: 120,
};

type SortKey = 'tableNumber' | 'remainingTime';
type SortDirection = 'ascending' | 'descending';

interface EditModalProps {
  table: TableRecord | null;
  onClose: () => void;
  onSave: (payload: { tableNumber: number; name: string; phone: string; partySize: number }) => void;
  loading: boolean;
}

const EditModal = ({ table, onClose, onSave, loading }: EditModalProps) => {
  const [formData, setFormData] = useState({
    tableNumber: '',
    name: '',
    phone: '',
    partySize: '',
  });

  useEffect(() => {
    if (!table) return;
    setFormData({
      tableNumber: String(table.tableNumber),
      name: table.name,
      phone: formatPhoneInput(table.phone),
      partySize: String(table.partySize),
    });
  }, [table]);

  if (!table) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h3>테이블 정보 수정</h3>
        <div className="form-group">
          <label>테이블 번호</label>
          <input
            name="tableNumber"
            value={formData.tableNumber}
            onChange={(e) => setFormData((current) => ({ ...current, tableNumber: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <label>대표자 이름</label>
          <input
            name="name"
            value={formData.name}
            onChange={(e) => setFormData((current) => ({ ...current, name: e.target.value }))}
          />
        </div>
        <div className="form-group">
          <label>전화번호</label>
          <input
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData((current) => ({ ...current, phone: formatPhoneInput(e.target.value) }))}
          />
        </div>
        <div className="form-group">
          <label>총 인원</label>
          <input
            name="partySize"
            type="number"
            value={formData.partySize}
            onChange={(e) => setFormData((current) => ({ ...current, partySize: e.target.value }))}
          />
        </div>
        <div className="modal-actions">
          <button type="button" className="ghost-button" onClick={onClose}>
            취소
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={() =>
              onSave({
                tableNumber: Number(formData.tableNumber),
                name: formData.name,
                phone: formData.phone,
                partySize: Number(formData.partySize),
              })
            }
          >
            {loading ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
};

function showBrowserNotification(title: string, body: string) {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'granted') {
    new Notification(title, { body, icon: '/favicon.ico' });
  }
}

const AdminDashboard = () => {
  const [token, setToken] = useState<string>(() => localStorage.getItem(ADMIN_TOKEN_KEY) || '');
  const [password, setPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [tables, setTables] = useState<TableRecord[]>([]);
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [settingsForm, setSettingsForm] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [sortKey, setSortKey] = useState<SortKey>('remainingTime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('ascending');
  const [editingTable, setEditingTable] = useState<TableRecord | null>(null);
  const [error, setError] = useState('');
  const [alarmMessage, setAlarmMessage] = useState('');
  const notifiedKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const timerId = window.setInterval(() => setCurrentTime(Date.now()), 1000);
    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    document.title = token ? `${settings.venueTitle} 관리자` : '공인대 주점 관리자';
  }, [settings.venueTitle, token]);

  const loadDashboard = useCallback(async (authToken = token, silent = false) => {
    if (!authToken) return;

    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.getAdminBootstrap(authToken);
      setTables(response.tables);
      setSettings(response.settings);
      setSettingsForm(response.settings);
      setError('');
    } catch (loadError) {
      const nextError = loadError instanceof Error ? loadError.message : '관리자 정보를 불러오지 못했습니다.';
      setError(nextError);
      if (nextError.includes('유효하지 않거나 만료된')) {
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken('');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    if (!token) return;

    loadDashboard(token);
    const pollId = window.setInterval(() => {
      loadDashboard(token, true);
    }, 10000);

    return () => window.clearInterval(pollId);
  }, [loadDashboard, token]);

  useEffect(() => {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const thresholds = [
      { minutes: 10, label: '10분 남음' },
      { minutes: 5, label: '5분 남음' },
      { minutes: 0, label: '이용시간 종료' },
    ];

    tables.forEach((table) => {
      const remainingMinutes = Math.floor((table.endTime - currentTime) / (60 * 1000));

      thresholds.forEach((threshold) => {
        const key = `${table.id}:${table.endTime}:${threshold.minutes}`;
        const shouldNotify =
          threshold.minutes === 0 ? remainingMinutes <= 0 : remainingMinutes <= threshold.minutes;

        if (!shouldNotify || notifiedKeysRef.current.has(key)) {
          return;
        }

        const message =
          threshold.minutes === 0
            ? `${table.tableNumber}번 테이블 이용시간이 종료되었습니다.`
            : `${table.tableNumber}번 테이블 이용시간 ${threshold.label}`;

        notifiedKeysRef.current.add(key);
        setAlarmMessage(message);
        showBrowserNotification(`${settings.venueTitle} 알림`, message);
        window.setTimeout(() => setAlarmMessage(''), 4000);
      });
    });
  }, [currentTime, settings.venueTitle, tables]);

  const sortedTables = useMemo(() => {
    return [...tables].sort((first, second) => {
      const firstValue =
        sortKey === 'tableNumber' ? first.tableNumber : first.endTime - currentTime;
      const secondValue =
        sortKey === 'tableNumber' ? second.tableNumber : second.endTime - currentTime;

      if (firstValue < secondValue) return sortDirection === 'ascending' ? -1 : 1;
      if (firstValue > secondValue) return sortDirection === 'ascending' ? 1 : -1;
      return 0;
    });
  }, [currentTime, sortDirection, sortKey, tables]);

  const requestSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((current) => (current === 'ascending' ? 'descending' : 'ascending'));
      return;
    }
    setSortKey(key);
    setSortDirection('ascending');
  };

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      setLoginLoading(true);
      const response = await api.adminLogin(password);
      localStorage.setItem(ADMIN_TOKEN_KEY, response.token);
      setToken(response.token);
      setPassword('');
      setLoginError('');
    } catch (loginErrorValue) {
      setLoginError(loginErrorValue instanceof Error ? loginErrorValue.message : '로그인에 실패했습니다.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setActionLoading(true);
      const response = await api.patchAdminSettings(token, settingsForm);
      setSettings(response.settings);
      setSettingsForm(response.settings);
      setError('');
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '설정 저장에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveEdit = async (payload: { tableNumber: number; name: string; phone: string; partySize: number }) => {
    if (!editingTable) return;

    try {
      setActionLoading(true);
      await api.patchAdminTable(token, editingTable.id, payload);
      setEditingTable(null);
      await loadDashboard(token, true);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : '테이블 수정에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdjustTime = async (table: TableRecord, minutes: number) => {
    try {
      setActionLoading(true);
      await api.patchAdminTable(token, table.id, { minutesDelta: minutes });
      await loadDashboard(token, true);
    } catch (adjustError) {
      setError(adjustError instanceof Error ? adjustError.message : '시간 조정에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (table: TableRecord) => {
    if (!window.confirm(`[${table.tableNumber}번 테이블] ${table.name}님 정보를 삭제하시겠습니까?`)) {
      return;
    }

    try {
      setActionLoading(true);
      await api.deleteAdminTable(token, table.id);
      await loadDashboard(token, true);
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : '테이블 삭제에 실패했습니다.');
    } finally {
      setActionLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem(ADMIN_TOKEN_KEY);
    setToken('');
    setTables([]);
  };

  if (!token) {
    return (
      <div className="admin-login-panel">
        <header className="page-header page-header--compact">
          <span className="page-badge">www.공인대주점.com/#admin</span>
          <h1>공인대 주점 관리자</h1>
          <p>관리자 비밀번호를 입력하면 테이블과 운영 설정을 수정할 수 있습니다.</p>
        </header>

        <form onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="adminPassword">관리자 비밀번호</label>
            <input
              id="adminPassword"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="비밀번호 입력"
              disabled={loginLoading}
            />
          </div>
          {loginError ? <p className="form-error">{loginError}</p> : null}
          <button type="submit" disabled={loginLoading}>
            {loginLoading ? '로그인 중...' : '관리자 로그인'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div>
      <header className="page-header page-header--compact">
        <span className="page-badge">www.공인대주점.com/#admin</span>
        <h1>{settings.venueTitle} 관리자</h1>
        <p>좌석 번호, 기본 이용 시간, 현재 사용 중인 테이블 시간을 한 화면에서 관리합니다.</p>
      </header>

      <div className="toolbar">
        <button type="button" className="ghost-button" onClick={() => loadDashboard(token)}>
          새로고침
        </button>
        <button type="button" className="ghost-button" onClick={logout}>
          로그아웃
        </button>
      </div>

      <section className="hero-panel hero-panel--admin">
        <div>
          <strong>현재 사용 중</strong>
          <span>{tables.length}개 테이블</span>
        </div>
        <div>
          <strong>기본 이용 시간</strong>
          <span>{settings.defaultDurationMinutes}분</span>
        </div>
        <div>
          <strong>최대 테이블</strong>
          <span>{settings.maxTableNumber}번</span>
        </div>
      </section>

      <section className="settings-card">
        <h3>운영 설정</h3>
        <div className="form-group">
          <label>브랜드명</label>
          <input
            value={settingsForm.venueTitle}
            onChange={(e) => setSettingsForm((current) => ({ ...current, venueTitle: e.target.value }))}
          />
        </div>
        <div className="settings-grid">
          <div className="form-group">
            <label>최대 테이블 번호</label>
            <input
              type="number"
              value={settingsForm.maxTableNumber}
              onChange={(e) =>
                setSettingsForm((current) => ({ ...current, maxTableNumber: Number(e.target.value) || 0 }))
              }
            />
          </div>
          <div className="form-group">
            <label>기본 이용 시간(분)</label>
            <input
              type="number"
              value={settingsForm.defaultDurationMinutes}
              onChange={(e) =>
                setSettingsForm((current) => ({ ...current, defaultDurationMinutes: Number(e.target.value) || 0 }))
              }
            />
          </div>
        </div>
        <button type="button" onClick={handleSaveSettings} disabled={actionLoading}>
          {actionLoading ? '저장 중...' : '설정 저장'}
        </button>
      </section>

      {alarmMessage ? <div className="alarm-banner">{alarmMessage}</div> : null}
      {error ? <p className="form-error">{error}</p> : null}

      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th onClick={() => requestSort('tableNumber')} className="sortable">
                테이블 {sortKey === 'tableNumber' ? (sortDirection === 'ascending' ? '▲' : '▼') : ''}
              </th>
              <th>대표자</th>
              <th>전화번호</th>
              <th>인원</th>
              <th onClick={() => requestSort('remainingTime')} className="sortable">
                남은 시간 {sortKey === 'remainingTime' ? (sortDirection === 'ascending' ? '▲' : '▼') : ''}
              </th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="empty-cell">목록을 불러오는 중입니다.</td>
              </tr>
            ) : sortedTables.length ? (
              sortedTables.map((table) => (
                <TableItem
                  key={table.id}
                  table={table}
                  currentTime={currentTime}
                  onEdit={setEditingTable}
                  onDelete={handleDelete}
                  onAdjustTime={handleAdjustTime}
                />
              ))
            ) : (
              <tr>
                <td colSpan={6} className="empty-cell">현재 사용 중인 테이블이 없습니다.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <EditModal table={editingTable} onClose={() => setEditingTable(null)} onSave={handleSaveEdit} loading={actionLoading} />
    </div>
  );
};

export default AdminDashboard;
